import { createSlice } from "@reduxjs/toolkit";
import {
  fetchUserById,
  fetchAllUsers,
  updateUserById,
  toggleUserStatus,
  deleteUserById,
  deleteUserAlls,
  addAdminNotes,
  fetchUserActivityLogs,
} from "./userThunks";

const initialState = {
  userData: null,
  userList: [],
  totalUsers: 0,
  totalPages: 1,
  // The paused filter the rows in userList were FETCHED with ("" | "only"),
  // set when a fetch lands. The list compares it with filters.pausedFilter,
  // so the old rows still on screen for the render after the filter changes
  // are never read as "the server ignored the filter" (review 2026-09-27).
  listPausedFilter: "",
  loading: false,
  error: null,
  activityLogs: [],

  filters: {
    currentPage: 1,
    itemsPerPage: 10,
    searchQuery: "",
    city: "",
    sortColumn: "createdAt",
    sortOrder: "desc",
    statusFilter: "",
    // "Missing" filter (2026-09-07): "" | name | age | height | location | photos
    missingFilter: "",
    // Introductions paused (2026-09-27): "" = everyone | "only" = paused members
    pausedFilter: "",
    // Name needs a look (2026-10-15): "" = everyone | "only" = flagged names
    nameReviewFilter: "",
    startDate: null,
    endDate: null,
  },
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUserData: (state) => {
      state.userData = null;
      state.error = null;
      state.activityLogs = [];
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH SINGLE USER
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.userData = action.payload;
        state.loading = false;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // FETCH USERS LIST
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.userList = action.payload.users || [];
        state.totalUsers = action.payload.metadata.totalUsers || 0;
        state.totalPages = action.payload.metadata.totalPages || 1;
        const asked = action.meta && action.meta.arg && action.meta.arg.params && action.meta.arg.params.paused;
        state.listPausedFilter = asked === "only" ? "only" : "";
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // UPDATE USER
      .addCase(updateUserById.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserById.fulfilled, (state, action) => {
        state.userData = action.payload;
        state.loading = false;
      })
      .addCase(updateUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // CHANGE STATUS
      .addCase(toggleUserStatus.pending, (state) => {
        // state.loading = true;
      })
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const user = state.userList.find((u) => u._id === id);
        if (user) user.status = status;
        // state.loading = false;
      })
      .addCase(toggleUserStatus.rejected, (state, action) => {
        // state.loading = false;
        state.error = action.payload;
      })
      // DELETE USER
      .addCase(deleteUserById.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserById.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.userList = state.userList.filter((u) => u._id !== deletedId);
        state.totalUsers = Math.max(0, state.totalUsers - 1); // decrement safely
        state.totalPages = Math.max(
          1,
          Math.ceil(state.totalUsers / state.filters.itemsPerPage)
        );
        state.loading = false;
      })
      .addCase(deleteUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Added Note
      .addCase(addAdminNotes.pending, (state) => {
        state.loading = true;
      })
      .addCase(addAdminNotes.fulfilled, (state, action) => {
        state.userData = action.payload;
        state.loading = false;
      })
      .addCase(addAdminNotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // DELETE All USER
      .addCase(deleteUserAlls.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserAlls.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.loading = false;
      })
      .addCase(deleteUserAlls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserActivityLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserActivityLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.activityLogs = action.payload.logs || [];
        state.activityPagination = action.payload.pagination || {};
      })
      .addCase(fetchUserActivityLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUserData, setFilters, resetFilters } = userSlice.actions;
export default userSlice.reducer;
